import { queryOptions } from "@tanstack/react-query";
import { config } from "@/config";

export interface ShareScene {
  scene_number: number;
  scene_name: string;
  seeing: string;
  hearing: string;
  props?: string;
  product?: string | null;
}

/** One Scene 1 hook; position 1 is the primary, whose copy is the Full Script. */
export interface ShareHook {
  position: number;
  voiceover: string;
  onScreenText: string | null;
}

export interface ShareStoryboardImage {
  model: string;
  /** null when the API could not sign the object — render nothing for it. */
  imageUrl: string | null;
}

export interface ShareFileDownload {
  /** Share-token path, or null when the upload is missing. */
  url: string | null;
  fileName: string | null;
}

export interface ShareBriefHook {
  hookIndex: number;
  direction: string;
  line: string;
}

export interface ShareBriefBlock {
  direction: string;
  line: string;
}

/** Public persona brief. Scripts omit `kind`; only this shape sets it. */
export interface ShareBriefPayload {
  kind: "brief";
  videoName: string;
  duration: string;
  hooks: ShareBriefHook[];
  concept: ShareBriefBlock;
  cta: ShareBriefBlock;
  notes: string;
  ageRange: string;
  gender: string;
  preferences: string;
  /** 1 = highlight the chosen hook; 4 = every hook is sent. */
  hookPackage: number;
  selectedHookIndex: number;
  updatedAt: string;
}

export type ShareLinkPayload = SharePayload | ShareBriefPayload;

export function isShareBriefPayload(data: ShareLinkPayload): data is ShareBriefPayload {
  return "kind" in data && data.kind === "brief";
}

export interface SharePayload {
  /** v:5 adds `hooks`. Older cached payloads omit `hooks`, and before v:4 also `fileDownload`. */
  v: 2 | 3 | 4 | 5;
  hook?: string | null;
  script?: string | null;
  scenes: ShareScene[];
  /** Persona scripts only, primary first; empty for legacy scripts. */
  hooks?: ShareHook[];
  concept?: string | null;
  duration?: string | null;
  presenter?: string | null;
  background?: string | null;
  product?: string | null;
  adDescription?: string | null;
  rationale?: string | null;
  originalBriefUrl?: string | null;
  sequence?: string | null;
  opportunityTitle?: string | null;
  /** Absent on payloads cached before the v:2 rollout; empty unless READY. */
  storyboardImages?: ShareStoryboardImage[];
  /** Present for an uploaded file or a script with no scenes and no body. */
  fileDownload?: ShareFileDownload | null;
  updatedAt: string;
}

// ─── Fetch (public, no auth) ─────────────────────────────────────────────────
// The token is the script or persona brief id — the share page is rendered live
// from the DB, so there is no create step. Scripts omit `kind`; briefs set it.

export const shareLinkKeys = {
  all: ["share-links"] as const,
  detail: (token: string) => [...shareLinkKeys.all, token] as const,
};

/** Absolute href for the share-token download path. Leaves already-absolute URLs alone. */
export function resolveShareFileHref(url: string): string {
  if (/^(https?:|data:)/i.test(url)) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${config.apiUrl}${path}`;
}

async function fetchShareLink(token: string): Promise<ShareLinkPayload> {
  // no-store: this URL is also the document the Share button opens. A normal
  // fetch would store the JSON under Cache-Control: public, and the next
  // window.open would render that cached JSON instead of the page.
  const res = await fetch(`${config.apiUrl}/s/${encodeURIComponent(token)}`, {
    cache: "no-store",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? `Share link not found (${res.status})`);
  }
  const json = await res.json() as { data: ShareLinkPayload };
  return json.data;
}

export function shareLinkQueryOptions(token: string) {
  return queryOptions({
    queryKey: shareLinkKeys.detail(token),
    queryFn: () => fetchShareLink(token),
    // Live DB read (Cache-Control: no-store). A 5 minute client cache kept a
    // pre-edit brief on screen after the Redis share cache was removed.
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
    enabled: !!token,
  });
}
